import { CreateCreditApplicationInput } from '@trnsact/trnsact-shared-types';
import { ExtractionFieldNode, ExtractionRecord, FieldEditGroup, FieldEditState } from '../types';

// Field key is typed against CreateCreditApplicationInput for refactor safety
export type ExtractionKey = keyof CreateCreditApplicationInput;

export interface ExtractionFieldConfig {
  label: string;
  key: ExtractionKey;
  format?: (v: unknown) => string;
}

export interface ExtractionFieldGroupConfig {
  groupLabel: string;
  fields: ExtractionFieldConfig[];
}

const currency = (v: unknown) =>
  `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Groups mirror the ticket's "personal identity / financial information / general financial
// background" breakdown. Labels sourced from DynamicOCA/config/fields_sectionized.json displayLabel.en
export const EXTRACTION_FIELD_GROUPS: ExtractionFieldGroupConfig[] = [
  {
    groupLabel: 'Applicant Information',
    fields: [
      { label: 'Legal Entity Name', key: 'businessName' },
      { label: 'DBA', key: 'dba' },
      { label: 'First Name', key: 'firstName' },
      { label: 'Last Name', key: 'lastName' },
      { label: 'Email', key: 'email' },
      { label: 'Phone Number', key: 'phone' },
      { label: 'Business Phone', key: 'bizPhone' },
      { label: 'Physical Business Address', key: 'address' },
      { label: 'City', key: 'city' },
      { label: 'State', key: 'state' },
      { label: 'Postal Code', key: 'postalCode' },
      { label: 'County', key: 'addressCounty' },
      { label: 'Country', key: 'country' },
    ],
  },
  {
    groupLabel: 'Financial Information',
    fields: [
      { label: 'Equipment Financing Amount Requested', key: 'amountRequested', format: currency },
      { label: 'Annual Revenue', key: 'annualRevenue', format: currency },
      { label: 'Term Requested', key: 'termRequested', format: v => `${v} months` },
    ],
  },
  {
    groupLabel: 'Business Background',
    fields: [
      { label: 'Legal Structure', key: 'legalStructure' },
      { label: 'Business Description', key: 'businessType' },
      { label: 'Time in Business', key: 'tib', format: v => `${v} years` },
      { label: 'Number of Employees', key: 'numOfEmployees' },
      { label: 'Equipment Make and Model', key: 'ccg_transaction_equipmentdescription' },
    ],
  },
];

export function confidenceColor(score: number): string {
  if (score >= 0.85) return '#4caf50';
  if (score >= 0.65) return '#ffa726';
  return '#ef5350';
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}

function buildFieldState(
  key: string,
  label: string,
  node: ExtractionFieldNode | undefined,
  format: ((v: unknown) => string) | undefined,
  edits: Record<string, string>
): FieldEditState {
  const originalValue = node?.value != null ? (format ? format(node.value) : String(node.value)) : '';
  const currentValue = edits[key] ?? originalValue;
  return {
    key,
    label,
    originalValue,
    currentValue,
    confidence: node?.confidenceScore ?? null,
    isDirty: currentValue !== originalValue,
  };
}

// Groups configured fields first, then buckets any remaining extraction keys (forward-compatible
// with the larger lender-side field set) into a catch-all group instead of dropping them.
export function buildFieldEditGroups(
  extraction: ExtractionRecord | null,
  edits: Record<string, string>
): FieldEditGroup[] {
  if (!extraction) return [];

  const usedKeys = new Set<string>();
  const groups = EXTRACTION_FIELD_GROUPS.map(({ groupLabel, fields }) => ({
    groupLabel,
    fields: fields.map(({ label, key, format }) => {
      usedKeys.add(key);
      return buildFieldState(key, label, extraction[key], format, edits);
    }),
  }));

  const overflowKeys = Object.keys(extraction).filter(key => key !== 'owners' && !usedKeys.has(key));
  if (overflowKeys.length > 0) {
    groups.push({
      groupLabel: 'Additional Extracted Fields',
      fields: overflowKeys.map(key => buildFieldState(key, humanizeKey(key), extraction[key], undefined, edits)),
    });
  }

  return groups;
}
